import React, { lazy, Suspense } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { trackEvent } from 'ddc-track-event';
import { Button } from 'react-bootstrap';
import Skeleton from 'wsm-skeleton';
import {
	useLabels,
	usePrefs,
	useRequestData,
	useFlags
} from 'ws-scripts/modules/common';
import { addImageParams } from '../../../utilities/images';
import { setModalSettings } from '../../../actions/modalSettings';
import {
	getTargetLocations,
	getVehicleLocationName
} from '../../../utilities/getTargetLocations';
import VehicleImage from './VehicleImage';
import Callout from '../Callout';
import './Slick.scss';
import './MediaContainer.scss';
import MediaSkeleton from '../skeleton/MediaSkeleton';

const LazyVehicleImageCarousel = lazy(() => import('./VehicleImageCarousel'));

const Media = ({
	enableMediaCarousel,
	isTabbedLayout,
	vehicle,
	accountPhone,
	loadingDelayed,
	usePlaceholderImage,
	ssrPlaceholderImage
}) => {
	const dispatch = useDispatch();
	const flags = useFlags();
	const labels = useLabels();
	const { deviceType, widgetName, windowId } = useRequestData();
	const {
		showVideoModalButton,
		openVideoModalLabel,
		restrictInsertLocationsForVehicles
	} = usePrefs();

	const targetLocations = getTargetLocations(
		vehicle,
		restrictInsertLocationsForVehicles
	);

	const vehicleMediaLocation = getVehicleLocationName(
		'vehicle-media',
		targetLocations,
		restrictInsertLocationsForVehicles
	);

	const { videos = [], uuid, title, link, images = [] } = vehicle;

	const phone = accountPhone;

	let videoButton = null;
	if (showVideoModalButton === 'true' && videos.length > 0) {
		videoButton = (
			<Button
				bsSize={deviceType === 'DESKTOP' ? 'xsmall' : null}
				className={`open-media-modal-btn ${
					deviceType === 'MOBILE' ? 'text-nowrap' : ''
				}`}
				onClick={() => {
					trackEvent(widgetName, windowId, {
						element: 'Video button',
						result: 'Video modal displayed'
					});

					dispatch(
						setModalSettings({
							isModalOpen: true,
							videos,
							uuid,
							title,
							link,
							phone
						})
					);
				}}
			>
				<i className="ddc-icon ddc-icon-play2 mr-2" />
				<span className="text-link text-link-muted">
					{labels.get(openVideoModalLabel)}
				</span>
			</Button>
		);
	}

	// Return false if _toggleMediaCarousel='false' added into URL param
	const showCarousel = flags['srp-use-media-carousel'] && enableMediaCarousel;

	const placeholderImageSrc =
		// ssrPlaceholderImage is set when SSR is enabled and vehicle index is 0
		ssrPlaceholderImage || (usePlaceholderImage && images[0]?.uri)
			? addImageParams(images[0].uri)
			: null;

	const mediaSkeleton = (
		<MediaSkeleton placeholderImage={placeholderImageSrc} />
	);
	// declare vehicleMedia as the skeleton loader
	let vehicleMedia = mediaSkeleton;
	// if loadingDelayed finished, replace skeleton loader with content
	if (!loadingDelayed) {
		// if content is a carousel with more than 1 image and useMediaCarousel FF is true
		if (showCarousel && images.length > 1) {
			vehicleMedia = (
				<Suspense fallback={mediaSkeleton}>
					<Skeleton
						skeletonClasses="mb-4 vehicle-image-skeleton"
						aspectRatio="4by3"
						placeholderImage={placeholderImageSrc}
					/>
					<LazyVehicleImageCarousel
						images={images}
						link={vehicle.link}
						uuid={vehicle.uuid}
						videoButton={videoButton}
					/>
				</Suspense>
			);
			// if content is a single image
		} else {
			vehicleMedia = (
				<VehicleImage
					image={images[0]}
					imageIndex={0}
					enableImage
					link={vehicle.link}
					uuid={vehicle.uuid}
					// isLoaded is used to indicate the image is fully loaded
					isLoaded={ssrPlaceholderImage !== null}
				/>
			);
		}
	}
	return (
		<>
			{vehicleMedia}
			{videoButton}
			{isTabbedLayout && (
				<ul
					className="list-unstyled d-flex flex-column vehicle-card-media align-items-center hidden"
					data-location={vehicleMediaLocation}
				/>
			)}
			{!isTabbedLayout && (
				<Callout
					callouts={vehicle.callout}
					vehicle={vehicle}
					{...{
						isTabbedLayout
					}}
				/>
			)}
		</>
	);
};

Media.propTypes = {
	enableMediaCarousel: PropTypes.bool.isRequired,
	vehicle: PropTypes.shape({
		images: PropTypes.arrayOf(PropTypes.shape({})),
		uuid: PropTypes.string,
		link: PropTypes.string,
		title: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
		phone: PropTypes.string,
		callout: PropTypes.arrayOf(PropTypes.shape({})),
		videos: PropTypes.arrayOf(PropTypes.shape({}))
	}).isRequired,
	isTabbedLayout: PropTypes.bool.isRequired,
	accountPhone: PropTypes.string.isRequired,
	loadingDelayed: PropTypes.bool,
	usePlaceholderImage: PropTypes.bool,
	inventoryIndex: PropTypes.number,
	ssrPlaceholderImage: PropTypes.string
};

Media.defaultProps = {
	loadingDelayed: false,
	ssrPlaceholderImage: null
};

export default Media;
